<!-- Created by init-app.js at Tue Mar 10 2020 16:02:41 GMT+0100 (GMT+01:00) -->
<template>
  <wgu-app-tpl>

    <!-- custom IDS help button -->
    <v-btn slot="wgu-tb-after-title"
      icon dense color="onprimary" @click="openHelpPage">
      <v-icon color="onprimary" medium>help</v-icon>
    </v-btn>

    <!-- custom IDS AGD logo -->
    <div slot="wgu-tb-end" class="ids-agd-logo-ct">
      <a href="https://agd.ids-mannheim.de/" target="_blank">
        <img src="static/img/agd_logo_weiss-1.png" alt="AGD Logo">
      </a>
    </div>

  </wgu-app-tpl>
</template>

<script>
  import Vue from 'vue';
  import WguAppTemplate from './WguAppTemplate.vue';
  import { Mapable } from '../src/mixins/Mapable';
  import { WguEventBus } from '../src/WguEventBus';
  import GeoJSON from 'ol/format/GeoJSON';
  import { Cluster, Vector as VectorSource } from 'ol/source';
  import { Vector as VectorLayer } from 'ol/layer';
  import { boundingExtent } from 'ol/extent';
  import DgdDataLoader from './ids/DgdDataLoader.js';
  import IdsStyleFactory from './ids/IdsStyleFactory';
  import JsUtil from './util/JsUtil';
  import IdsCorpusLookup from './ids/IdsCorpusLookup.js';

  import FilterPanel from './ids/components/filter/IdsFilterModuleWin.vue';

  export default {
    name: 'my-wgu-app',
    mixins: [Mapable],
    components: {
      'wgu-app-tpl': WguAppTemplate,
      'ids-filter-panel': FilterPanel
    },
    // add Vue methods and hooks here
    props: {
      clusterDistance: { type: Number, required: false, default: 20 },
      clusterMinDistance: { type: Number, required: false, default: 10 }
    },
    data () {
      return {
        resultLayer: null,
        resultSource: null,
        corpusList: [],
        initLocationsLoaded: false,
        initFilterDataLoaded: false,
        helpPageLink: 'https://agd.ids-mannheim.de'
      }
    },
    async created () {
      // Apply app config
      const appConfig = Vue.prototype.$appConfig;
      // set to true in static/app-conf.json to get local API mock data
      Vue.prototype.$devMode = appConfig.ids.devMode;
      // optional overwrite of the API base URL from app config
      const baseUrlConf = appConfig.ids.baseUrl;
      if (baseUrlConf && JsUtil.isString(baseUrlConf) && baseUrlConf !== '') {
        DgdDataLoader.baseUrl = Vue.prototype.$appConfig.ids.baseUrl;
      }
      const corpLinkTpl = appConfig.ids.corpusLinkTpl;
      if (corpLinkTpl && JsUtil.isString(corpLinkTpl) && corpLinkTpl !== '') {
        IdsCorpusLookup.corpusLinkTpl = corpLinkTpl;
      }
      const helpPageLink = appConfig.ids.helpPageLink;
      if (helpPageLink && JsUtil.isString(helpPageLink) && helpPageLink !== '') {
        this.helpPageLink = helpPageLink;
      }

      // set initial corpus IDs for map data and selection in filter UI
      if (appConfig.ids.initialCorpusIds && Array.isArray(appConfig.ids.initialCorpusIds)) {
        IdsCorpusLookup.initialCorpusIds = appConfig.ids.initialCorpusIds;
      }

      // set cluster distance (min. distance in pixels between clusters)
      this.clusterDistance = JsUtil.isNumeric(appConfig.ids.mapClusterDistance)
        ? parseInt(appConfig.ids.mapClusterDistance) : this.clusterDistance;

      // END Apply app config

      // signal that filter base data is loaded (corpus list, ...)
      WguEventBus.$on('ids-filter-base-data-load', () => {
        this.initFilterDataLoaded = true;
        this.checkForInitialDataLoaded();
      });

      // workaround timeout of 1 to make this work
      setTimeout(() => {
        // hide and decrease size of the sidebar for small mobile screens
        if (this.$vuetify.breakpoint.xs) {
          WguEventBus.$emit('sidebar-toggle');
        }
      }, 1);
    },
    methods: {
      async onMapBound () {
        // show loading mask on startup
        WguEventBus.$emit('app-loading-mask-toggle', true);

        WguEventBus.$on('ids-new-locations-load', this.updateLocationLayer);

        this.createResultClusterLayer();

        // load base data, such as corpus list
        await this.loadInitialData();

        this.loadInitialLocations();

        this.registerMapClick();

        this.setInitialFilters();
      },

      /**
       * Loads the initial data
       * default dataset (Ortsdaten) for the application and
       * adds it to the source of the result layer.
       */
      async loadInitialData () {
        // load all corpus
        this.corpusList = await DgdDataLoader.getCorpusList();
        // save in lookup, so we can access data app-wide
        IdsCorpusLookup.setCorpusList(this.corpusList);
        WguEventBus.$emit('ids-corpus-list-load');
        // TODO load more data if needed ...
      },

      /**
       * Creates a clustered vector layer for the results data (Ortsdaten) and
       * adds it to the map.
       */
      createResultClusterLayer () {
        const source = new VectorSource({}); // empty vector source
        const clusterSource = new Cluster({
          distance: this.clusterDistance,
          source: source
        });

        // de-cluster helper layer
        const declusterSource = new VectorSource();
        const declusterLayer = new VectorLayer({
          name: 'ids-decluster-layer',
          lid: 'ids-decluster-layer',
          source: declusterSource
        });
        this.map.addLayer(declusterLayer);

        const clusters = new VectorLayer({
          lid: 'ids-result-layer',
          source: clusterSource
        });
        this.map.addLayer(clusters);

        const styleCache = {};
        const clusterStyle = IdsStyleFactory.createClusterStyle(styleCache, this.map);

        clusters.setStyle(clusterStyle);

        // make accessible as members
        this.locationLayer = clusters;
        this.locationSource = source;
        this.locationDeclusterLayer = declusterLayer;
        this.locationDeclusterSource = declusterLayer.getSource();
      },

      /**
       * Registers a click handler on the map in order to zoom to cluster
       * features when clicking a cluster.
       */
      registerMapClick () {
        // zoom to cluster features on click
        this.map.on('click', (e) => {
          this.locationLayer.getFeatures(e.pixel).then((clickedClusterFeats) => {
            if (clickedClusterFeats.length) {
              // get clustered coordinates
              const features = clickedClusterFeats[0].get('features');
              // currently points at same location prevent this
              if (features.length > 1) {
                const extent = boundingExtent(
                  features.map((r) => r.getGeometry().getCoordinates())
                );
                // zoom to cluster extent
                this.map.getView().fit(extent, { duration: 1000, padding: [50, 50, 50, 50], maxZoom: 18 });
              }
            }
          });
        });
      },

      /**
       * Loads the initial data
       * default dataset (Ortsdaten) for the application and
       * adds it to the source of the result layer.
       */
      async loadInitialLocations () {
        const initCorpusIds = IdsCorpusLookup.initialCorpusIds;
        const initCorpusSelectionObj = IdsCorpusLookup.getSelectionObjectByArr(initCorpusIds);
        // TODO make all defaults configurable
        const locationType = ['Aufnahmeort'];
        const personType = 'Sprecher';
        // load 'Ortsdaten' as GeoJSON
        const geoJsonData = await DgdDataLoader.getLocationList(initCorpusSelectionObj, locationType, personType);

        this.updateLocationLayer(geoJsonData);

        // signal that initial locations are loaded
        WguEventBus.$emit('ids-init-locations-load');

        this.initLocationsLoaded = true;
        this.checkForInitialDataLoaded();
      },

      /**
       * Checks if the initial data is loaded and hides the loading mask
       */
      checkForInitialDataLoaded () {
        if (this.initLocationsLoaded && this.initFilterDataLoaded) {
          // hide loading mask
          WguEventBus.$emit('app-loading-mask-toggle', false);
        }
      },

      /**
       * Handler for app-wide 'ids-new-locations-load' event
       * (see IdsFilterModuleWin).
       * Updates the location layer with the given GeoJSON features.
       *
       * @param {Object} GeoJSON FeatureCollection
       */
      async updateLocationLayer (geoJsonData) {
        // parse GeoJSON
        const geojsonParser = new GeoJSON({ featureProjection: 'EPSG:3857' });
        const features = geojsonParser.readFeatures(geoJsonData);
        // clear old features
        this.locationSource.clear();
        this.locationDeclusterSource.clear();
        // add new features
        this.locationSource.addFeatures(features);
        // zoom map to new features
        const extent = this.locationSource.getExtent();
        this.map.getView().fit(extent, { duration: 1000, padding: [50, 50, 50, 50], maxZoom: 18 });
      },

      /**
       * Sets the initial filters in our global app-wide filter state object.
       */
      setInitialFilters () {},

      /**
       * Opens the help page by opening the link in a new browser tab.
       */
      openHelpPage () {
        window.open(this.helpPageLink, '_blank');
      }
    }
  }
</script>

<style scoped>

  .ids-agd-logo-ct {
    margin-top: 8px;
  }

</style>



// WEBPACK FOOTER //
// app/WguApp.vue